/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import EmberObject, { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import MedicalConditionType from 'linced-app/enums/medical-condition-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import MedicalAccess from 'linced-app/enums/medical-access';
import {
  findBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';

export default class MedicalConditionManager extends Component {
  // Services
  @service
  options;

  @service
  permissions;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @service
  toaster;

  MedicalConditionType = MedicalConditionType;
  Jurisdiction = Jurisdiction;
  MedicalAccess = MedicalAccess;
  Scopes = Scopes;
  @tracked readOnly = false;
  expiredConditionsExpanded = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('fieldGroups.[]')
  get groups() {
    return this.fieldGroups.filter(
      (group) => group.personType === PersonType.LEARNER,
    );
  }

  @computed('groups.[]')
  get conditionGroup() {
    return this.groups.find((group) => group.key === 'medicalCondition');
  }

  @computed('session.scopes.[]', 'readOnly')
  get canAddMedicalConditions() {
    return !this.readOnly && this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @tracked conditionGroupFields = Object.freeze([
    'core:subtype',
    'core:actionPlans',
    'core:autoinjectorHeldOnsite',
    'core:isDiagnosedByDoctor',
    'core:doctorWhoDiagnosed',
    'core:hospitalised',
    'core:hospitalName',
  ]);

  roleOptionTypes = MedicalAccess.values;

  // conditionGroupFieldsResolved resolves conditionGroup.fields to its
  // loaded records: that hasMany is a PromiseManyArray, and calling array
  // methods (filter, slice, findBy's for..of) directly on it is deprecated.
  @computed('conditionGroup.fields.content.[]')
  get conditionGroupFieldsResolved() {
    return resolved(this.conditionGroup?.fields);
  }

  @computed('conditionGroupFieldsResolved.[]', 'conditionGroupFields')
  get conditionGroupExtraFields() {
    return this.conditionGroupFieldsResolved.filter(
      (field) => !this.conditionGroupFields.includes(field.key),
    );
  }

  @computed('conditionGroupFieldsResolved.[]')
  get subtypeField() {
    return findBy(this.conditionGroupFieldsResolved, 'key', 'core:subtype');
  }

  @computed('conditionGroupFieldsResolved.[]')
  get actionPlansField() {
    return findBy(this.conditionGroupFieldsResolved, 'key', 'core:actionPlans');
  }

  @computed('conditionGroupFieldsResolved.[]')
  get diagnosedByDoctorField() {
    return findBy(
      this.conditionGroupFieldsResolved,
      'key',
      'core:isDiagnosedByDoctor',
    );
  }

  @computed('conditionGroupFieldsResolved.[]')
  get doctorWhoDiagnosedField() {
    return findBy(
      this.conditionGroupFieldsResolved,
      'key',
      'core:doctorWhoDiagnosed',
    );
  }

  @computed('conditionGroupFieldsResolved.[]')
  get hospitalisedField() {
    return findBy(
      this.conditionGroupFieldsResolved,
      'key',
      'core:hospitalised',
    );
  }

  @computed('conditionGroupFieldsResolved.[]')
  get hospitalNameField() {
    return findBy(
      this.conditionGroupFieldsResolved,
      'key',
      'core:hospitalName',
    );
  }

  @computed('conditionGroupFieldsResolved.[]')
  get autoInjectorField() {
    return findBy(
      this.conditionGroupFieldsResolved,
      'key',
      'core:autoinjectorHeldOnsite',
    );
  }

  @computed('conditionGroupFieldsResolved.[]')
  get autoinjectorLabel() {
    const label = 'Adrenaline Autoinjector Held Onsite';
    let field = findBy(
      this.conditionGroupFieldsResolved,
      'key',
      'core:autoinjectorHeldOnsite',
    );
    if (isEmpty(field)) {
      return label;
    }
    return field.label;
  }

  @tracked
  conditionDialogOpen = false;

  @computed('editingCondition', 'conditionIsReadOnly')
  get conditionDialogTitle() {
    if (this.conditionIsReadOnly) {
      return 'Medical Condition Details';
    }
    if (this.editingCondition) {
      return 'Edit Medical Condition';
    }
    return 'Add Medical Condition';
  }

  @computed(
    'jurisdiction',
    'conditionType',
    'conditionIsReadOnly',
    'condition.fields.[]',
  )
  get conditionTypeOptions() {
    if (
      this.jurisdiction === Jurisdiction.AU_NSW &&
      !this.conditionIsReadOnly
    ) {
      return MedicalConditionType.values.find((value) => {
        return (
          value.value === MedicalConditionType.SHORT_TERM_MEDICAL_CONDITION
        );
      });
    }
    return this.conditionType;
  }

  conditionDetailsOpen = false;

  @tracked
  editingCondition = false;

  @tracked
  conditionType = null;

  @action
  setConditionType(value) {
    this.conditionType = value;
  }

  @tracked
  conditionPriority = 0;

  @action
  setConditionPriority(value) {
    this.conditionPriority = value;
  }

  @tracked
  conditionDescription = '';

  @action
  setConditionDescription(value) {
    this.conditionDescription = value;
  }

  @tracked
  conditionHighRisk = false;

  @action
  setConditionHighRisk(value) {
    this.conditionHighRisk = value;
  }

  @tracked
  conditionAnaphylaxis = false;

  @action
  setConditionAnaphylaxis(value) {
    this.conditionAnaphylaxis = value;
  }

  @tracked
  conditionMedicationHeld = false;

  @action
  setConditionMedicationHeld(value) {
    this.conditionMedicationHeld = value;
  }

  @tracked
  conditionStartDate = '';

  @action
  setConditionStartDate(value) {
    this.conditionStartDate = value;
  }

  @tracked
  conditionReviewDate = '';

  @action
  setConditionReviewDate(value) {
    this.conditionReviewDate = value;
  }

  @tracked
  conditionEndDate = '';

  @action
  setConditionEndDate(value) {
    this.conditionEndDate = value;
  }

  @tracked
  conditionRoleRequired = MedicalAccess.None;

  @action
  setConditionRoleRequired(value) {
    this.conditionRoleRequired = value;
  }

  @tracked
  condition = null;

  defaultValue = EmberObject.create({
    label: 'Minor Health Condition',
    value: 'Minor Health Condition',
  });

  @computed('readOnly', 'condition.externalId')
  get conditionIsReadOnly() {
    // Don't allow editing if the whole manager is marked read-only, or if the
    // condition has an external ID (i.e. is synced in).
    return this.readOnly || !isEmpty(this.get('condition.externalId'));
  }

  @tracked
  conditionToRemove = null;

  @tracked
  confirmRemoveConditionDialogOpen = false;

  @action
  newCondition() {
    let fragment = this.store.createFragment('people/medical-condition', {
      description: '',
    });
    this.resetForm();
    this.condition = fragment;
    this.conditionDialogOpen = true;
    this.editingCondition = false;
  }

  @action
  editCondition(condition) {
    this.conditionType = findBy(
      MedicalConditionType.values,
      'value',
      condition.type,
    );
    this.conditionPriority = condition.priority;
    this.conditionDescription = condition.description;
    this.conditionHighRisk = condition.highRisk;
    this.conditionAnaphylaxis = condition.anaphylaxis;
    this.conditionMedicationHeld = condition.prescribedMedicationHeld;
    this.conditionStartDate = condition.startDate;
    this.conditionReviewDate = condition.reviewDate;
    this.conditionEndDate = condition.endDate;
    this.conditionRoleRequired = findBy(
      this.roleOptionTypes,
      'value',
      condition.roleRequired,
    );
    this.condition = condition;
    this.editingCondition = true;
    this.conditionDialogOpen = true;
  }

  @action
  closeConditionDialog() {
    if (this.condition && this.condition.hasDirtyAttributes) {
      this.condition.rollbackAttributes();
    }
    this.conditionDialogOpen = false;
    this.resetForm();
  }

  @action
  saveCondition() {
    // Set the core properties.
    this.condition.setProperties({
      description: this.conditionDescription,
      priority: this.conditionPriority,
      highRisk: this.conditionHighRisk,
      anaphylaxis: this.conditionAnaphylaxis,
      prescribedMedicationHeld: this.conditionMedicationHeld,
      startDate: this.conditionStartDate,
      reviewDate: this.conditionReviewDate,
      endDate: this.conditionEndDate,
      roleRequired: this.conditionRoleRequired.value,
    });

    // Set the type conditionally based on jurisdiction
    if (this.jurisdiction === Jurisdiction.AU_NSW) {
      // default the subtype to 'minor health condition' if condition is short term
      this.condition
        .get('fields.core:subtype')
        .set('value', 'Minor Health Condition');
      // Set the type to SHORT_TERM_MEDICAL_CONDITION
      this.condition.set(
        'type',
        MedicalConditionType.SHORT_TERM_MEDICAL_CONDITION,
      );
    } else {
      this.condition.set('type', this.conditionType.value);
    }

    this.get('person.learner.medicalConditions').addFragment(this.condition);
    this.conditionDialogOpen = false;
    this.resetForm();
  }

  @action
  showField(conditionTypeValue, field) {
    return !(
      !this.conditionIsReadOnly &&
      this.jurisdiction === Jurisdiction.AU_NSW &&
      (field === this.actionPlansField || field === this.autoInjectorField)
    );
  }

  @action
  handleCloseConditionDetails() {
    this.closeConditionDetails();
  }

  @action
  showConfirmRemoveCondition(condition) {
    this.conditionToRemove = condition;
    this.confirmRemoveConditionDialogOpen = true;
  }

  @action
  confirmRemoveCondition(shouldRemove) {
    if (shouldRemove) {
      this.get('person.learner.medicalConditions').removeFragment(
        this.conditionToRemove,
      );
    }
    this.conditionToRemove = null;
    this.confirmRemoveConditionDialogOpen = false;
  }

  @action
  reset() {
    this.resetForm();
  }

  resetForm() {
    this.conditionType = null;
    this.conditionPriority = 0;
    this.conditionDescription = '';
    this.conditionHighRisk = false;
    this.conditionAnaphylaxis = false;
    this.conditionMedicationHeld = false;
    this.conditionStartDate = '';
    this.conditionReviewDate = '';
    this.conditionEndDate = '';
    this.conditionRoleRequired = findBy(
      this.roleOptionTypes,
      'value',
      MedicalAccess.NONE,
    );
    this.condition = null;
  }
}
